<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type SubjectOfBusiness from '@/types/SubjectOfBusiness'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'

const props = withDefaults(
  defineProps<{
    open: boolean
    subject: SubjectOfBusiness | null
    catalog: SubjectOfBusiness[]
    saving?: boolean
  }>(),
  { saving: false }
)

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'submit', value: SubjectOfBusiness): void
}>()

const isOpen = computed({
  get: () => props.open,
  set: (val: boolean) => emit('update:open', val),
})

const form = ref({
  id: 0,
  title: '',
  description: '',
  price: 0,
  category_id: 0,
  ui_category_id: 0,
})

const isEditMode = computed(() => props.subject !== null)

watch(
  () => props.open,
  (open) => {
    if (open) resetForm()
  }
)

watch(
  () => props.subject,
  (subject) => {
    if (subject) {
      form.value = {
        id: subject.id,
        title: subject.title,
        description: subject.description,
        price: subject.price,
        category_id: subject.category_id,
        ui_category_id: uiCategoryId(subject.ui_category_id),
      }
    }
  },
  { immediate: true }
)

function resetForm() {
  if (props.subject) {
    form.value = {
      id: props.subject.id,
      title: props.subject.title,
      description: props.subject.description,
      price: props.subject.price,
      category_id: props.subject.category_id,
      ui_category_id: uiCategoryId(props.subject.ui_category_id),
    }
  } else {
    form.value = {
      id: 0,
      title: '',
      description: '',
      price: 0,
      category_id: 0,
      ui_category_id: 0,
    }
  }
}

function uiCategoryId(value: number | number[] | undefined): number {
  if (value === undefined || value === null) return 0
  return Array.isArray(value) ? value[0] ?? 0 : value
}

function onCatalogChange(value: any) {
  const cat = props.catalog.find((c) => c.id === Number(value))
  if (cat) {
    form.value.title = cat.title
    form.value.description = cat.description
    form.value.price = cat.price
    form.value.category_id = cat.category_id
    form.value.ui_category_id = uiCategoryId(cat.ui_category_id)
  }
}

function onSubmit() {
  emit('submit', { ...form.value })
  emit('update:open', false)
}
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogContent class="max-w-md">
      <DialogHeader>
        <DialogTitle>
          {{ isEditMode ? 'Upraviť predmet podnikania' : 'Pridať predmet podnikania' }}
        </DialogTitle>
      </DialogHeader>
      <div class="px-4 py-5 sm:p-6">
        <div v-if="!isEditMode" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Predmet z katalógu
          </label>
          <FormKit
            type="select"
            v-model="form.category_id"
            class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
            @input="onCatalogChange"
          >
            <option value="">Vyberte predmet z katalógu</option>
            <option
              v-for="cat in catalog"
              :key="cat.id"
              :value="cat.id"
            >
              {{ cat.title }}
            </option>
          </FormKit>
        </div>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              for="title"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Názov
            </label>
            <FormKit
              type="text"
              v-model="form.title"
              name="title"
              id="title"
              class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
            />
          </div>
          <div>
            <label
              for="description"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Popis
            </label>
            <FormKit
              type="text"
              v-model="form.description"
              name="description"
              id="description"
              class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
            />
          </div>
          <div>
            <label for="price" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Cena
            </label>
            <FormKit
              type="text"
              number
              v-model="form.price"
              name="price"
              id="price"
              class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
            />
          </div>
          <div>
            <label
              for="category_id"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Kategória
            </label>
            <FormKit
              type="text"
              number
              v-model="form.category_id"
              name="category_id"
              id="category_id"
              class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
            />
          </div>
          <div>
            <label
              for="ui_category_id"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              UI Kategória
            </label>
            <FormKit
              type="text"
              number
              v-model="form.ui_category_id"
              name="ui_category_id"
              id="ui_category_id"
              class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700"
            />
          </div>
        </div>
      </div>
      <div class="flex flex-row px-4 py-3 justify-between bg-gray-50 text-right sm:px-6 dark:bg-white/3">
        <Button
          variant="outline"
          @click="emit('update:open', false)"
          class="focus:outline-none text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:ring-red-300 font-medium rounded-lg text-sm px-5 py-2.5 mr-2 mb-2 dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-900"
        >
          Zrušiť
        </Button>
        <Button
          type="submit"
          @click="onSubmit"
          :disabled="saving"
          class="focus:outline-none text-white bg-brand-600 hover:bg-brand-700 focus:ring-4 focus:ring-brand-500 font-medium rounded-lg text-sm px-5 py-2.5 mr-2 mb-2"
        >
          Uložiť
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
